import {
  Middleware,
  Reducer,
  StoreEnhancer,
  configureStore as configureStoreReact,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import createSagaMiddleware, { Saga } from "redux-saga";

import { getPreloadedStateFromCookie } from "./authUtils";
import createReduxEnhancerOptions from "./createReduxEnhancerOptions";
import heartbeatMiddleware from "../middleware/heartbeatMiddleware";
import timestampMiddleware from "../middleware/timestampMiddleware";

const configurePWStore = ({
  rootReducer,
  rootSaga,
  ignoredStatePaths,
  middleware,
  enhancers,
}: {
  rootReducer: Reducer;
  rootSaga: Saga;
  ignoredStatePaths?: string[];
  middleware?: Array<Middleware>;
  enhancers?: Array<StoreEnhancer>;
}) => {
  const sagaMiddleware = createSagaMiddleware();

  const sentryReduxEnhancer = Sentry.createReduxEnhancer(
    createReduxEnhancerOptions
  );

  const ignoredPaths = (ignoredStatePaths || []).concat([
    "requestStatuses.errors",
  ]);

  const store = configureStoreReact({
    reducer: rootReducer,
    preloadedState: getPreloadedStateFromCookie(),
    devTools:
      process.env.NODE_ENV !== "production"
        ? {
            stateSanitizer: (state: any) =>
              ignoredPaths.reduce(
                (sanitizedState, statePath) => ({
                  ...sanitizedState,
                  [statePath]: "<<LONG_BLOB>>",
                }),
                state
              ),
          }
        : false,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredPaths,
          ignoredActionPaths: ["resolve", "reject", "payload.error"],
        },
        immutableCheck: {
          ignoredPaths,
        },
      })
        .concat([timestampMiddleware, heartbeatMiddleware, sagaMiddleware])
        .concat(middleware || []),
    enhancers: [sentryReduxEnhancer].concat(enhancers || []),
  });

  sagaMiddleware.run(rootSaga);

  return store;
};

export default configurePWStore;
